/**
 * Focus kept across content drawn again. When a signal's content is replaced
 * by the same content drawn from new values, as a kept row's component is
 * when its row takes a new object, the element that had focus is gone with
 * the old nodes. The element at the same place in the new nodes, if it is
 * the same kind of element, takes focus instead, with the caret where it was:
 * someone typing into a field that is drawn again as they type goes on typing.
 * A list's row built again for a new object under its key is kept the same way.
 */
import { hasGlobal } from '../ir/guards';

/** Where the focused element sits: which of the nodes holds it, then the child index at each step down. */
export interface FocusPlace {
  readonly path: readonly number[];
  readonly tag: string;
  readonly type: string | null;
  readonly selection:
    | readonly [number, number, 'forward' | 'backward' | 'none']
    | null;
}

function hasSelection(
  element: Element,
): element is HTMLInputElement | HTMLTextAreaElement {
  if (
    !(element instanceof HTMLInputElement) &&
    !(element instanceof HTMLTextAreaElement)
  )
    return false;
  // Inputs such as number and email have no selection, and reading it throws.
  try {
    return element.selectionStart !== null;
  } catch {
    return false;
  }
}

/** The place of the focused element among the nodes, or null when focus is elsewhere. */
export function focusPlaceAmong(nodes: readonly Node[]): FocusPlace | null {
  if (!hasGlobal('document')) return null;
  const active = document.activeElement;
  if (!active || active === document.body) return null;
  const top = nodes.findIndex((node) => node.contains(active));
  const holder = nodes[top];
  if (!holder) return null;
  const path: number[] = [];
  for (let step: Node = active; step !== holder;) {
    const parent: Node | null = step.parentNode;
    if (!parent) return null;
    path.unshift(Array.prototype.indexOf.call(parent.childNodes, step));
    step = parent;
  }
  path.unshift(top);
  const selection = hasSelection(active)
    ? ([
        active.selectionStart ?? 0,
        active.selectionEnd ?? 0,
        active.selectionDirection ?? 'none',
      ] as const)
    : null;
  return {
    path,
    tag: active.tagName,
    type: active.getAttribute('type'),
    selection,
  };
}

/** The place of the focused element among the nodes between `start` and `end`. */
export function focusPlace(start: Node, end: Node): FocusPlace | null {
  const nodes: Node[] = [];
  for (
    let node = start.nextSibling;
    node && node !== end;
    node = node.nextSibling
  )
    nodes.push(node);
  return focusPlaceAmong(nodes);
}

/** Focuses the element at the same place in the new nodes, when it is the same kind of element. */
export function restoreFocus(place: FocusPlace, nodes: readonly Node[]): void {
  const [top, ...rest] = place.path;
  let node: Node | undefined = top === undefined ? undefined : nodes[top];
  for (const index of rest) {
    node = node?.childNodes[index];
    if (!node) return;
  }
  if (
    !(node instanceof HTMLElement) ||
    node.tagName !== place.tag ||
    node.getAttribute('type') !== place.type
  )
    return;
  node.focus({ preventScroll: true });
  if (place.selection && hasSelection(node)) {
    const [from, to, direction] = place.selection;
    node.setSelectionRange(from, to, direction);
  }
}
